import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '../ui/Button';
interface OccasionCalendarProps {
  onDateSelect: (date: Date) => void;
  selectedDate: Date | null;
}
export function OccasionCalendar({
  onDateSelect,
  selectedDate
}: OccasionCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };
  const firstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };
  const generateCalendarDays = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Actual days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const isPast = date < today;
      const isSelected = selectedDate && date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
      days.push({
        day,
        date,
        isPast,
        isSelected
      });
    }
    return days;
  };
  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };
  const days = generateCalendarDays();
  return <div className="bg-white rounded-2xl shadow-xl border border-rose-100 p-6 max-w-md w-full" data-id="element-892">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-6" data-id="element-893">
        <button onClick={previousMonth} className="p-2 hover:bg-rose-50 rounded-lg transition-colors text-gray-600 hover:text-rose-500" data-id="element-894">
          <ChevronLeft className="w-5 h-5" data-id="element-895" />
        </button>

        <h3 className="font-serif text-xl font-bold text-gray-900" data-id="element-896">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <button onClick={nextMonth} className="p-2 hover:bg-rose-50 rounded-lg transition-colors text-gray-600 hover:text-rose-500" data-id="element-897">
          <ChevronRight className="w-5 h-5" data-id="element-898" />
        </button>
      </div>

      {/* Day Labels */}
      <div className="grid grid-cols-7 gap-2 mb-2" data-id="element-899">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2" data-id="element-900">
            {day}
          </div>)}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-2" data-id="element-901">
        {days.map((dayData, index) => {
        if (!dayData) {
          return <div key={`empty-${index}`} className="aspect-square" data-id="element-902" />;
        }
        return <motion.button key={`day-${dayData.day}`} whileHover={!dayData.isPast ? {
          scale: 1.1
        } : {}} whileTap={!dayData.isPast ? {
          scale: 0.95
        } : {}} onClick={() => !dayData.isPast && onDateSelect(dayData.date)} disabled={dayData.isPast} className={`
                aspect-square rounded-lg font-medium text-sm transition-all
                ${dayData.isPast ? 'text-gray-300 cursor-not-allowed' : dayData.isSelected ? 'bg-rose-500 text-white shadow-lg' : 'text-gray-700 hover:bg-rose-50 hover:text-rose-600'}
              `} data-id="element-903">
              {dayData.day}
            </motion.button>;
      })}
      </div>

      {/* Selected Date Display */}
      {selectedDate && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mt-6 p-4 bg-rose-50 rounded-xl border border-rose-100" data-id="element-904">
          <div className="flex items-center justify-center gap-2 text-rose-600" data-id="element-905">
            <CalendarIcon className="w-4 h-4" data-id="element-906" />
            <span className="font-medium" data-id="element-907">
              {selectedDate.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
            </span>
          </div>
        </motion.div>}
    </div>;
}